<template>
  <!-- eslint-disable max-len -->
  <div>
    <main
      id="main"
      ref="main"
      :class="{'confirm-order mobile': true,
               'ivs-failed': failedIvsVerification }"
    >
      <div id="exp-banner" />
      <section class="u-container pb-0">
        <h1 class="font-bold text-4xl">
          Official USPS<sup>®</sup> Change-of-Address
        </h1>
        <ProgressBar
          v-if="!showChoiceExperience"
          :steps="6"
          :current="6"
        />
        <IndividualProgressBarWithSteps
          v-if="showChoiceExperience"
          :mobile="true"
        />
      </section>
      <div id="content">
        <section class="u-container--x mt-2">
          <h2
            id="step-heading"
            ref="stepHeading"
            class="text-xl"
          >
            <template v-if="!showChoiceExperience">
              Step 6:&nbsp;
            </template>
            <span class="font-bold confirmation-heading">Confirmation</span>
          </h2>
          <template v-if="mcoa">
            <p class="mt-4">
              There is one more step to complete: check your email.<br>You'll receive an email confirmation from us soon. In the email, click on the provided link to continue and finalize your Change-of-Address process.
            </p>
          </template>
          <template v-else>
            <div
              id="confirm-order-default-banner"
              class="p-4 bg-gray-100 mt-4"
            >
              <h3 class="text-lg font-bold">
                Your Change-of-Address request has been submitted!
              </h3>
              <p class="mt-4">
                <template v-if="showCfpsId">
                  Your confirmation code is <strong ref="cfpsId">{{ cfpsId | formatCfpsId }}</strong>.
                </template>
                <span class="confirmation-sub-text">
                  You will receive a confirmation email shortly with more information. See below for more information and next steps about your Change-of-Address.
                </span>
              </p>
            </div>
            <div
              v-show="flagAccessHandoffEnabled"
              id="amp-success-page"
              class="mt-2"
            >
              <div
                v-show="offersSegment"
                id="amp-offers"
                ref="offers"
              >
                <OffersIndFamTemp v-show="offersIndFamTempSegment" />
                <OffersBiz v-show="offersBizSegment" />
              </div>
              <div
                v-show="voterRegSegment"
                id="amp-voter-reg"
                ref="voterReg"
                class="mt-6"
              >
                <MobileVoterReg />
              </div>
            </div>
            <!-- Access Handoff Disabled -->
            <div
              v-if="!flagAccessHandoffEnabled"
              ref="accessDisabled"
              class="access-disabled"
            >
              <p class="access-disabled__copy">
                <strong>Next, tackle everything on your moving to-do list with MYMOVE.</strong> By clicking CONTINUE you will be leaving USPS.com and redirected to the MYMOVE website. Once you leave, you will be subject to the MYMOVE Privacy Policy and Terms and Conditions.
              </p>
              <a
                id="continue-mymove"
                href="https://www.mymove.com/moving/"
                class="c-btn w-full mt-6"
              >
                Continue
              </a>
            </div>
          </template>
          <div
            id="hair-line"
            class="c-hairline mt-6"
          />
        </section>
        <section>
          <div class="text-center mt-6 pb-6">
            <router-link
              id="change-another-address"
              ref="changeAnotherAddress"
              :to="{ name: 'disclaimer' }"
              class="c-link"
            >
              Need to change another address?
            </router-link>
          </div>
          <div
            v-if="!mcoa"
            id="important-info-content"
            class="bg-gray-100 text-base u-container important__notice"
          >
            <p>
              <strong>Important Information About Your Change-of-Address Request:</strong>
            </p>
            <ol
              ref="list"
              class="list-decimal px-4 mt-6"
            >
              <li v-if="showCfpsId">
                <strong>You should receive a mailed communication from the U.S. Postal Service at your new address within 7-10 business days of your mail forwarding date.</strong> Please retain this Official Change-of-Address Confirmation page for your records as local agencies and/or resources may require it for proof of your move.
              </li>
              <li class="mt-6 font-bold">
                Please retain this Confirmation Code as it is required to view, edit, or cancel your Change-of-Address.
              </li>
              <li class="mt-6">
                <strong>You will receive a Move Validation Letter at your old address.</strong> For your protection, this will not contain your new address.
              </li>
            </ol>
          </div>
          <p
            v-if="!mcoa"
            id="important-info-notice"
            class="u-container text-xs"
          >
            Neither the U.S. Postal Service<sup>®</sup> nor any contractor associated with the operation of this website: (1) make any guarantee as to the accuracy of the statements made by the advertisers or the content of the editorial material on this website; (2) endorse any company, product or service listed or advertised in this MoversGuide Online<sup>™</sup> product; or (3) will be held liable in any manner for any claim, loss, expense, damage or consumer dissatisfaction arising out of or in connection with the use of the MoversGuide Online<sup>™</sup> product.
          </p>
        </section>
      </div>
    </main>
  </div>
</template>

<script>
import {
  ProgressBar,
  IndividualProgressBarWithSteps,
} from '@mgo/ui/lib/components';
import Cookies from 'js-cookie';
import { mobileAnalytics } from './analytics';
import confirmOrderMixin from './mixin';
import OffersBiz from '../../splits/ConfirmOrder/MobileOffersBiz.vue';
import OffersIndFamTemp from '../../splits/ConfirmOrder/MobileOffersIndFamTemp.vue';
import MobileVoterReg from '../../splits/ConfirmOrder/MobileVoterReg.vue';

export default {
  name: 'ConfirmOrder',
  components: {
    OffersBiz,
    OffersIndFamTemp,
    MobileVoterReg,
    ProgressBar,
    IndividualProgressBarWithSteps,
  },
  mixins: [confirmOrderMixin],
  computed: {
    showNonControlExperience: () => JSON.parse(sessionStorage.getItem('showNonControlExperience')) ?? false,
    showChoiceExperience: () => JSON.parse(sessionStorage.getItem('showChoiceExperience')),
    analytics() {
      return mobileAnalytics();
    },
    userNeedsReprompt() {
      return !this.autofill
        && !this.repromptShown
        && !this.showReprompt;
    },
  },
  async mounted() {
    if (!this.cfpsId && !this.dcoa) {
      this.$router.push({ name: 'disclaimer' });
      this.$store.commit('DISABLE_LOADER');
      return;
    }
    if (this.flagAmpersendEnabled && this.flagAccessHandoffEnabled) {
      this.$amp.veil(this.$refs.main);
    }
    await this.hydrateCOA();
    if (this.flagAmpersendEnabled && this.flagAccessHandoffEnabled) {
      await this.getAmpersendExperiences();
      this.$amp.unveil(this.$refs.main);
    }
    this.$store.commit('DISABLE_LOADER');

    if (this.cfpsId !== '') {
      this.$coa.reset();
      this.$deleteSession();
      Cookies.remove('coa');
      Cookies.remove('coa_complete');
      localStorage.removeItem('ampersend_data');
    }
  },
};
</script>
